import Vue from "vue"
import customerHubApi from "@/api/customerHubApi"
import userService from "@/services/userService"
import { store } from "@/store/stores"

const trainingPreferences = ["auto", "plans", "courses", "both"]
const accessError = "Deine Sitzung oder Berechtigung ist nicht mehr gültig. Bitte öffne die App erneut."
const connectionError = "Die Ansicht konnte nicht gespeichert werden. Bitte versuche es erneut."

// This grant and the account preferences are deliberately never persisted in
// Vuex or localStorage. Training/Watch/Health access remains a separate service.
class CustomerHubPreferences {
  constructor() {
    this.state = Vue.observable({
      allowed: false, ready: false, loading: false, saving: false,
      userId: "", useNewView: true, trainingPreference: "auto", error: "", revision: 0,
    })
    this.token = ""
    this.version = 0
    this.pending = null
    this.activeWrites = new Map()
    this.handleRefresh = () => { this.refresh() }
  }

  identity() {
    try { return { userId: String(userService.getUserId() || ""), token: localStorage.getItem("token") || "" } }
    catch (_) { return { userId: "", token: "" } }
  }

  isAllowed() {
    const current = this.identity()
    return this.state.ready && this.state.allowed && Boolean(current.userId && current.token) &&
      this.state.userId === current.userId && this.token === current.token
  }

  isEnabled() { return this.isAllowed() && this.state.useNewView }

  update(values) {
    // Busy/error changes do not invalidate unrelated reads such as bookings.
    const grantChanged = ["allowed", "ready", "userId", "useNewView"].some((key) =>
      Object.prototype.hasOwnProperty.call(values, key) && this.state[key] !== values[key])
    Object.assign(this.state, values)
    if (grantChanged) this.state.revision++
  }

  isCurrent(request) {
    const current = this.identity()
    return request.version === this.version && request.userId === current.userId && request.token === current.token
  }

  reset(ready) {
    this.version++
    this.pending = null
    this.token = ""
    this.update({ allowed: false, ready, loading: false, saving: false, userId: "", useNewView: true, trainingPreference: "auto", error: "" })
  }

  invalidate() { this.reset(false) }
  clear() { this.reset(true) }

  begin(userId, token) {
    if (this.state.userId !== userId || this.token !== token) this.invalidate()
    this.token = token
    const request = { userId, token, version: ++this.version, promise: null }
    this.pending = request
    this.update({ userId, loading: true, error: "" })
    return request
  }

  deny(userId, token, error = "") {
    if (this.state.userId !== userId || this.token !== token) this.invalidate()
    this.version++
    this.pending = null
    this.token = token
    this.update({ userId, allowed: false, ready: true, loading: false, saving: false, error })
  }

  parsePreferences(result, userId) {
    if (!result || result.success !== true || String(result.userId || "") !== userId ||
      typeof result.hasAccess !== "boolean" || typeof result.useNewView !== "boolean" ||
      !trainingPreferences.includes(result.trainingPreference)) {
      const error = new Error("Invalid customer hub preferences")
      error.customerHubInvalidResponse = true
      throw error
    }
    return result
  }

  applyPreferences(result) {
    this.update({ allowed: result.hasAccess, ready: true, loading: false, saving: false,
      useNewView: result.useNewView, trainingPreference: result.trainingPreference, error: "" })
  }

  async loadPreferences(request) {
    try {
      const result = await customerHubApi.getPreferences(request.token)
      if (!this.isCurrent(request)) return null
      const preferences = this.parsePreferences(result, request.userId)
      this.applyPreferences(preferences)
      return preferences
    } catch (error) {
      if (this.isCurrent(request)) this.deny(request.userId, request.token,
        [401, 403].includes(error?.response?.status) ? accessError : "Die neue Ansicht konnte nicht geladen werden. Bitte versuche es erneut.")
      return null
    } finally {
      if (this.pending === request) this.pending = null
    }
  }

  // Only App.initialize or a fresh rights request may call this with a grant.
  // A Vuex mutation can revoke a grant, but must never create one.
  resolve(userId, token, rights) {
    userId = String(userId || "")
    const current = this.identity()
    if (!userId || !token || current.userId !== userId || current.token !== token) return Promise.resolve(null)
    if (!Array.isArray(rights) || !rights.includes("customer_hub_v2")) {
      this.deny(userId, token)
      return Promise.resolve(null)
    }
    const activeWrite = this.activeWrites.get(userId)
    if (activeWrite) {
      if (this.isCurrent(activeWrite)) return activeWrite.promise.catch(() => null)
      // A renewed login to the same account waits for its earlier write, then
      // reads the final server value. A different account remains independent.
      return activeWrite.promise.catch(() => null).then(() => this.resolve(userId, token, rights))
    }
    const request = this.begin(userId, token)
    request.promise = this.loadPreferences(request)
    return request.promise
  }

  refresh() {
    const { userId, token } = this.identity()
    if (!userId || !token) { this.clear(); return Promise.resolve(null) }
    if (this.state.userId !== userId || this.token !== token) this.invalidate()
    if (this.pending && this.isCurrent(this.pending)) return this.pending.promise
    const activeWrite = this.activeWrites.get(userId)
    if (activeWrite) {
      if (this.isCurrent(activeWrite)) return activeWrite.promise.catch(() => null)
      return activeWrite.promise.catch(() => null).then(() => this.refresh())
    }
    const request = this.begin(userId, token)
    request.promise = this.refreshRequest(request)
    return request.promise
  }

  async refreshRequest(request) {
    try {
      const rights = await customerHubApi.getRights(request.token)
      if (!this.isCurrent(request)) return null
      if (!Array.isArray(rights) || !rights.includes("customer_hub_v2")) {
        this.deny(request.userId, request.token)
        return null
      }
      return await this.loadPreferences(request)
    } catch (_) {
      if (this.isCurrent(request)) this.deny(request.userId, request.token, accessError)
      return null
    } finally {
      if (this.pending === request) this.pending = null
    }
  }

  async savePreferences(values) {
    if (!this.isAllowed()) throw new Error(accessError)
    if (this.activeWrites.has(this.state.userId)) throw new Error("Bitte warte, bis die Ansicht gespeichert wurde.")
    const keys = values && typeof values === "object" && !Array.isArray(values) ? Object.keys(values) : []
    if (!keys.length || keys.some((key) => !["useNewView", "trainingPreference"].includes(key)) ||
      (keys.includes("useNewView") && typeof values.useNewView !== "boolean") ||
      (keys.includes("trainingPreference") && !trainingPreferences.includes(values.trainingPreference))) {
      throw new Error("Die gewählte Ansicht ist ungültig.")
    }
    // Supersede an earlier GET and serialize writes. Keep the last confirmed
    // values until this account's successful PUT has returned.
    const request = { ...this.identity(), version: ++this.version, promise: null }
    this.pending = null
    this.activeWrites.set(request.userId, request)
    this.update({ saving: true, loading: false, error: "" })
    request.promise = this.saveRequest(request, { ...values })
    return request.promise
  }

  async saveRequest(request, values) {
    try {
      const result = await customerHubApi.savePreferences(values, request.token)
      if (!this.isCurrent(request)) throw new Error("Deine Sitzung hat sich geändert. Bitte prüfe die gewählte Ansicht erneut.")
      const preferences = this.parsePreferences(result, request.userId)
      if (!preferences.hasAccess) {
        this.deny(request.userId, request.token, accessError)
        throw new Error(accessError)
      }
      this.applyPreferences(preferences)
      return preferences
    } catch (error) {
      if (this.isCurrent(request)) {
        if ([401, 403].includes(error?.response?.status) || error.customerHubInvalidResponse) this.deny(request.userId, request.token, accessError)
        else this.update({ saving: false, error: connectionError })
      }
      throw error
    } finally {
      if (this.activeWrites.get(request.userId) === request) this.activeWrites.delete(request.userId)
    }
  }

  start() {
    if (this.unsubscribe) return
    this.unsubscribe = store.subscribe((mutation) => {
      if (mutation.type !== "userStore/setUser" && mutation.type !== "userStore/setUserRights") return
      const current = this.identity()
      if (current.userId !== this.state.userId || current.token !== this.token) this.invalidate()
      if (mutation.type === "userStore/setUserRights" &&
        (!Array.isArray(mutation.payload) || !mutation.payload.includes("customer_hub_v2"))) {
        this.resolve(current.userId, current.token, [])
      }
    })
    window.addEventListener("online", this.handleRefresh)
  }

  stop() {
    this.unsubscribe?.(); this.unsubscribe = null
    window.removeEventListener("online", this.handleRefresh)
    this.invalidate()
  }
}

export default new CustomerHubPreferences()
